import { createTheme } from '@material-ui/core';

declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    sidebarWidth: number;
    sidebarMobileHeight: number;
    brand: string;
    footerHeight: number;
    mobileTopBarHeight: number;
    mobileFooterHeight: number;
    sidebarMobilePadding: number;
    participantBorderWidth: number;
    rightDrawerWidth: number;
  }

  // allow configuration using `createMuiTheme`
  interface ThemeOptions {
    sidebarWidth?: number;
    sidebarMobileHeight?: number;
    brand: string;
    footerHeight: number;
    mobileTopBarHeight: number;
    mobileFooterHeight: number;
    sidebarMobilePadding: number;
    participantBorderWidth: number;
    rightDrawerWidth?: number;
  }
}

const defaultTheme = createTheme();

export default createTheme({
  overrides: {
    MuiButton: {
      root: {
        borderRadius: '4px',
        textTransform: 'none',
        color: 'rgb(40, 42, 43)',
        fontSize: '0.9rem',
        transition: defaultTheme.transitions.create(['background-color', 'box-shadow', 'border', 'color'], {
          duration: defaultTheme.transitions.duration.short,
        }),
      },
      text: {
        padding: '6px 14px',
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      outlinedPrimary: {
        border: '2px solid #027AC5',
        '&:hover': {
          border: '2px solid rgb(1, 85, 137)',
        },
      },
      startIcon: {
        marginRight: '6px',
      },
    },
    MuiTypography: {
      body1: {
        color: 'rgb(40, 42, 43)',
        fontSize: '0.9rem',
      },
    },
    MuiInputBase: {
      root: {
        fontSize: '0.9rem',
      },
    },
    MuiSelect: {
      root: {
        padding: '0.85em',
      },
    },
    MuiDialogActions: {
      root: {
        padding: '16px',
      },
    },
    MuiTextField: {
      root: {
        color: 'rgb(40, 42, 43)',
      },
    },
    MuiInputLabel: {
      root: {
        color: 'rgb(40, 42, 43)',
        fontSize: '1.1rem',
        marginBottom: '0.2em',
        fontWeight: 500,
      },
    },
    MuiOutlinedInput: {
      notchedOutline: {
        borderColor: 'rgb(136, 140, 142)',
      },
    },

    // Safari Modal Fixes
    MuiDialog: {
      root: {
        zIndex: 1500,
      },
      container: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      paper: {
        backgroundColor: '#ffffff',
        color: '#000000',
        borderRadius: 16,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.24)',
        margin: 16,
        maxHeight: 'calc(100vh - 32px)',
        overflowY: 'auto',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        // Safari animation fix
        WebkitAnimation: 'safari-modal-enter 0.2s ease-out',
        animation: 'safari-modal-enter 0.2s ease-out',
        // Force hardware acceleration
        WebkitTransform: 'translateZ(0)',
        transform: 'translateZ(0)',
        '@media (max-width: 600px)': {
          minWidth: 300,
          maxWidth: 'calc(100vw - 32px)',
          margin: 16,
        },
      },
    },
    MuiDialogTitle: {
      root: {
        color: '#202124',
        fontWeight: 500,
        padding: '24px 24px 16px',
        fontSize: 20,
        lineHeight: 1.3,
      },
    },
    MuiDialogContent: {
      root: {
        color: '#5f6368',
        padding: '0 24px 24px',
        fontSize: 14,
        lineHeight: 1.5,
      },
    },
    // MuiDialogActions: {
    //   root: {
    //     padding: '0 24px 16px',
    //     justifyContent: 'center',
    //   },
    // },
    MuiBackdrop: {
      root: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -1,
      },
    },
    MuiCssBaseline: {
      '@global': {
        'html, body, #root': {
          height: '100%',
        },
        // Safari modal animation keyframes
        '@-webkit-keyframes safari-modal-enter': {
          from: {
            opacity: 0,
            transform: 'scale(0.9)',
          },
          to: {
            opacity: 1,
            transform: 'scale(1)',
          },
        },
        '@keyframes safari-modal-enter': {
          from: {
            opacity: 0,
            transform: 'scale(0.9)',
          },
          to: {
            opacity: 1,
            transform: 'scale(1)',
          },
        },
        // Safari list fixes for modal content
        '.MuiDialog-paper ol': {
          margin: 0,
          paddingLeft: 0,
          listStyle: 'none',
          counterReset: 'step-counter',
        },
        '.MuiDialog-paper ol li': {
          position: 'relative',
          paddingLeft: 24,
          marginBottom: 12,
          color: '#5f6368',
          fontSize: 14,
          lineHeight: 1.4,
          '&:before': {
            content: 'counter(step-counter)',
            counterIncrement: 'step-counter',
            position: 'absolute',
            left: 0,
            top: 0,
            backgroundColor: '#027AC5', // Using your primary color
            color: '#ffffff',
            width: 18,
            height: 18,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            fontWeight: 'bold',
          },
        },
        // Safari scrollbar fixes
        '.MuiDialog-paper::-webkit-scrollbar': {
          width: 8,
        },
        '.MuiDialog-paper::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: 4,
        },
        '.MuiDialog-paper::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: 4,
          '&:hover': {
            background: '#a8a8a8',
          },
        },
      },
    },
  },
  typography: {
    fontFamily: 'Inter, sans-serif',
  },
  palette: {
    primary: {
      main: '#027AC5',
    },
  },
  brand: '#E22525',
  footerHeight: 72,
  mobileFooterHeight: 56,
  sidebarWidth: 300,
  sidebarMobileHeight: 90,
  sidebarMobilePadding: 8,
  participantBorderWidth: 2,
  mobileTopBarHeight: 52,
  rightDrawerWidth: 320,
});
